import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  SimpleChanges,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, SortDirection, MatSortHeader } from '@angular/material/sort';
import { SafeHtml } from '@angular/platform-browser';
import { AppDialogRef, AppDialogService } from '../services/app-dialog.service';
import {
  ColumnConfigurationComponent,
  ColumnOption,
} from './column-configuration/column-configuration.component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { fromEvent, merge, Observable, of, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Dayjs } from 'dayjs';
import { CustomAction } from './customAction';
import { Column } from './column';
import { CustomTableAction } from './customTableAction';
import { IconAction } from './iconAction';
import { BaseModel } from '../models/basemodel';
import { NimmstaDataSource } from './nimmstadata.datasource';
import { DataTableModuleConfiguration } from './datatable-module.config';
import { LanguageService } from '../services/language.service';
import {
  NgClass,
  AsyncPipe,
  TitleCasePipe,
  NgTemplateOutlet,
} from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { LaddaModule } from 'angular2-ladda';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ErrorComponent } from '../components/error/error.component';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { FilterEmitterType } from './filter/abstract-filter-element.component';
import {
  AbstractFilterElement,
  FilterElementType,
  FilterElementSize,
} from './filter/filter-elements';
import {
  BooleanFilterElementComponent,
  DateRangeFilterElementComponent,
  ResultFieldFilterElementComponent,
  ResultFieldMultiFilterElementComponent,
  SelectionFilterElementComponent,
  TextSearchFilterElementComponent,
} from './filter';
import {
  isResultColumn,
  isValueTruncated,
  resolveColumnValue,
  truncateCellValue,
} from './data-table-value.utils';

type RecursiveElement = RecursiveObject | RecursiveObjectLeafData;
type RecursiveObject = { [key: string]: RecursiveElement };
type RecursiveObjectLeafData =
  string | number | boolean | null | undefined | Dayjs;

interface DeletePreviewField {
  label: string;
  translateLabel: boolean;
  value: string | SafeHtml;
  translateValue: boolean;
  renderValueAsHtml: boolean;
}

interface DeletePreview {
  primary: DeletePreviewField;
  details: DeletePreviewField[];
}

/**
 * Data Table can be used to display data in a table.
 */
@Component({
  selector: 'app-datatable',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  imports: [
    TranslatePipe,
    LaddaModule,
    NgClass,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    ErrorComponent,
    AsyncPipe,
    TitleCasePipe,
    TranslatePipe,
    NgTemplateOutlet,
    SelectionFilterElementComponent,
    BooleanFilterElementComponent,
    TextSearchFilterElementComponent,
    DateRangeFilterElementComponent,
    ResultFieldFilterElementComponent,
    ResultFieldMultiFilterElementComponent,
  ],
})
export class DataTableComponent<T extends BaseModel>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private static readonly RESULT_CELL_TRUNCATE_LIMIT = 80;
  private static readonly SINGLE_COLUMN_BREAKPOINT = 550;
  private static readonly LANDSCAPE_MOBILE_BREAKPOINT = 992;
  private static readonly LANDSCAPE_MOBILE_COLUMN_COUNT = 4;

  @Input() dataSource!: NimmstaDataSource<T>;

  @Input() columns: Column[] = [];

  /**
   * Optional full set of base columns for column configuration.
   * Use when `columns` is already filtered to visible columns.
   */
  @Input() baseColumns: Column[] = [];

  @Input() identifier?: string;

  @Input() actionNew = true;

  customActions = input<CustomAction<T>[]>([]);

  iconActions = input<IconAction<T>[]>([]);

  @Input() btnClass = 'primary';

  customTableActions = input<CustomTableAction<T>[]>([]);

  // Secondary tables should for example not update query parameters overwriting the main table's parameters
  isMainTable = input<boolean>(true);

  filterElements = input<AbstractFilterElement[]>([]);

  @Input() loadOnInit = true;

  @Input() searchEnabled = true;

  @Input() searchPlaceholder = 'search';

  @Input() showPagination = true;

  @Input() compactPortraitColumnCount = 1;

  @Input() compactPortraitColumns: string[] = [];

  /**
   * When true, custom filters will be positioned beside the search field instead of in a separate row below.
   * This creates a more compact layout suitable for tables with fewer filters.
   * @default false
   */
  @Input() filtersBesideSearch = false;

  @Input() defaultPageSize?: number;

  @Input() defaultSortName?: string;

  @Input() defaultSortDirection?: 'asc' | 'desc';

  /**
   * When true, enables the column configuration feature with a gear icon button.
   * Requires `identifier` to be set to save/load column configurations.
   */
  @Input() columnConfigurationEnabled = false;

  /**
   * Available custom field options for column configuration (e.g., result fields).
   * These are displayed in the column configuration dialog alongside base columns.
   */
  customFieldOptions = input<ColumnOption[]>([]);

  /**
   * Emits when the user saves a new column configuration.
   * The parent component should update its columns based on the new configuration.
   */
  @Output() columnConfigurationChanged = new EventEmitter<string[]>();

  @Output() showEvent = new EventEmitter<string>();

  @Output() newEvent = new EventEmitter<void>();

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  @ViewChild('input', { static: false }) input!: ElementRef;

  protected modalRef?: AppDialogRef;

  protected customTableActionsText = computed(() =>
    this.customTableActions().map((action) => action.getText()),
  );

  protected actualDisplayedColumns = signal<Column[]>([]);

  protected headerDef = computed(() =>
    this.actualDisplayedColumns().map((c) => c.displayedProperty),
  );

  protected error?: HttpErrorResponse;

  protected processedData: {
    [key: string]: { [key: string]: WritableSignal<string | undefined> };
  } = {};

  protected customActionActivationMap: { [key: string]: Signal<boolean>[] } =
    {};

  protected customActionVisibilityMap: { [key: string]: Signal<boolean>[] } =
    {};

  protected hasActivatedCustomNonPrimaryMap: {
    [key: string]: Signal<boolean>;
  } = {};

  protected iconActionActivationMap: { [key: string]: Signal<boolean>[] } = {};

  protected iconActionVisibilityMap: { [key: string]: Signal<boolean>[] } = {};

  protected customActionTextMap: { [key: string]: string[] } = {};

  protected iconActionTextMap: { [key: string]: string[] } = {};

  protected customActionClassMap: { [key: string]: string[] } = {};

  protected iconActionClassMap: { [key: string]: string[] } = {};

  private deleteId?: string;

  private deleteEvent?: EventEmitter<string>;

  private storageKeyAppendix = '_table_sort_page_filter';

  private sortParamKey = 'sort';

  private directionParamKey = 'dir';

  private pageSizeParamKey = 'pageSize';

  private pageParamKey = 'page';

  private loadSub!: Subscription;

  private loadDataSub?: Subscription;

  private clickActionBlacklist = ['a', 'button'];

  private reloadInterval?: number;

  private reloadTimeout?: ReturnType<typeof setTimeout>;

  private destroyed = false;

  protected customFilters: Record<string, string> = {};

  protected resultFilters: Record<string, string> = {};

  private soloFilterActive = false;

  private reloadOnError = true;

  private previousColumnsSignature = '';

  private modalService = inject(AppDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private configuration = inject(DataTableModuleConfiguration);
  private languageService = inject(LanguageService);
  private cdr = inject(ChangeDetectorRef);

  protected readonly FilterElementType = FilterElementType;

  ngOnInit(): void {
    this.checkWidth();
    this.paginator.pageSizeOptions = this.configuration.defaultPageSizeOptions;
    this.paginator.pageSize =
      this.defaultPageSize ?? this.configuration.defaultPageSize;
    this.languageService.languageChange.subscribe(() => {
      this.setUpProcessedDataMirror();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const columnsChange = changes['columns'];
    if (!columnsChange) {
      return;
    }

    const nextColumns = columnsChange.currentValue ?? [];
    const nextSignature = this.buildColumnsSignature(nextColumns);

    // Many parent components expose columns via getters, which creates new array instances on each
    // change detection pass. Ignore equivalent shapes to avoid repeatedly rebuilding async cell values.
    if (nextSignature === this.previousColumnsSignature) {
      return;
    }

    this.previousColumnsSignature = nextSignature;
    this.checkWidth();
    if (this.dataSource?.count) {
      this.setUpProcessedDataMirror();
    }
  }

  protected checkWidth(): void {
    const deduplicatedColumns = this.getDeduplicatedColumns();
    if (!deduplicatedColumns.length) {
      return;
    }

    const selectedColumns = this.selectResponsiveColumns(deduplicatedColumns);
    const columnsToSet = this.withActionsColumn(selectedColumns);
    this.actualDisplayedColumns.set(columnsToSet);
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.checkWidth();
    if (this.dataSource?.count) {
      this.setUpProcessedDataMirror();
    }
  }

  private getDeduplicatedColumns(): Column[] {
    const seen = new Set<string>();
    return this.columns.filter((column) => {
      if (seen.has(column.displayedProperty)) {
        return false;
      }
      seen.add(column.displayedProperty);
      return true;
    });
  }

  private selectResponsiveColumns(columns: Column[]): Column[] {
    if (this.isCompactPortraitMobile()) {
      return this.selectCompactPortraitColumns(columns);
    }

    if (this.isLandscapeMobile()) {
      return columns.slice(
        0,
        Math.min(
          columns.length,
          DataTableComponent.LANDSCAPE_MOBILE_COLUMN_COUNT,
        ),
      );
    }

    return columns;
  }

  private selectCompactPortraitColumns(columns: Column[]): Column[] {
    const columnCount = Math.max(1, this.compactPortraitColumnCount);
    if (!this.compactPortraitColumns.length) {
      return columns.slice(0, Math.min(columns.length, columnCount));
    }

    const columnsByProperty = new Map(
      columns.map((column) => [column.displayedProperty, column]),
    );
    const selectedColumns: Column[] = [];
    const seen = new Set<string>();

    for (const displayedProperty of this.compactPortraitColumns) {
      const column = columnsByProperty.get(displayedProperty);
      if (!column || seen.has(displayedProperty)) {
        continue;
      }

      selectedColumns.push(column);
      seen.add(displayedProperty);
      if (selectedColumns.length >= columnCount) {
        return selectedColumns;
      }
    }

    for (const column of columns) {
      if (seen.has(column.displayedProperty)) {
        continue;
      }

      selectedColumns.push(column);
      seen.add(column.displayedProperty);
      if (selectedColumns.length >= columnCount) {
        break;
      }
    }

    return selectedColumns;
  }

  private withActionsColumn(columns: Column[]): Column[] {
    if (columns.some((column) => column.displayedProperty === 'actions')) {
      return columns;
    }
    return [...columns, new Column('actions')];
  }

  private isLandscapeMobile(): boolean {
    return (
      window.matchMedia('(orientation: landscape)').matches &&
      window.innerWidth <= DataTableComponent.LANDSCAPE_MOBILE_BREAKPOINT
    );
  }

  private isCompactPortraitMobile(): boolean {
    return (
      window.innerWidth < DataTableComponent.SINGLE_COLUMN_BREAKPOINT &&
      !this.isLandscapeMobile()
    );
  }

  ngAfterViewInit(): void {
    this.loadSortPageFilterSearch();
    if (this.loadOnInit) {
      this.loadDataPage();
    }

    if (this.input?.nativeElement) {
      fromEvent(this.input.nativeElement, 'keyup')
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe({
          next: () => {
            this.paginator.pageIndex = 0;
            this.saveSortPageFilterSearch();
            this.changeQueryParams();
            this.loadDataPage();
          },
        });

      // Search reset emits the 'search' event, why I don't know.
      // Never gets fired otherwise
      fromEvent(this.input.nativeElement, 'search')
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe({
          next: () => {
            this.paginator.pageIndex = 0;
            this.saveSortPageFilterSearch();
            this.changeQueryParams();
            this.loadDataPage();
          },
        });
    }

    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.saveSortPageFilterSearch();
      this.changeQueryParams();
    });

    this.paginator.page.subscribe(() => {
      this.saveSortPageFilterSearch();
      this.changeQueryParams();
    });

    this.loadSub = merge(this.paginator.page, this.sort.sortChange)
      .pipe(debounceTime(500))
      .subscribe({
        next: () => {
          this.loadDataPage();
        },
      });
  }

  private findSortingName(active: string): string {
    const column = this.columns.find((c) => c.displayedProperty === active);
    return column?.sortingName ?? active;
  }

  ngOnDestroy(): void {
    this.loadSub.unsubscribe();
    this.loadDataSub?.unsubscribe();
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }
    this.destroyed = true;
  }

  loadDataPage(removeDataOnLoad = false): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
    }
    this.loadDataSub?.unsubscribe();
    this.loadDataSub = this.dataSource
      .loadData({
        search: this.input?.nativeElement?.value || '',
        filter: Object.values(this.customFilters),
        resultFilters: Object.values(this.resultFilters),
        sortColumn: this.findSortingName(this.sort.active),
        sortOrder: this.getSort(),
        pageNumber: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        removeDataOnLoad,
      })
      .subscribe({
        next: () => {
          if (!this.destroyed) {
            this.error = undefined;
            // Recompute active columns before building processed cell values.
            // Otherwise newly-loaded columns (e.g. custom result fields) can appear blank until next reload.
            this.checkWidth();
            this.setUpActionMaps();
            this.setUpProcessedDataMirror();
            this.changeQueryParams();
            this.paginator.length = this.dataSource.count;
            if (this.reloadInterval) {
              this.reloadTimeout = setTimeout(() => {
                this.loadDataPage();
              }, this.reloadInterval);
            }
            this.reloadOnError = true;
          }
        },
        error: (e: HttpErrorResponse) => {
          const lowercaseMessage = e?.error?.exc_message?.toLowerCase();
          if (
            e.status === 500 &&
            this.reloadOnError &&
            lowercaseMessage &&
            lowercaseMessage.includes('no property') &&
            lowercaseMessage.includes('found for type')
          ) {
            this.resetQuery();
            return;
          }
          this.reloadOnError = true;
          this.error = e;
        },
      });
  }

  private setUpActionMaps(): void {
    this.customActionActivationMap = {};
    this.customActionVisibilityMap = {};
    this.customActionTextMap = {};
    this.customActionClassMap = {};
    this.iconActionActivationMap = {};
    this.iconActionVisibilityMap = {};
    this.iconActionTextMap = {};
    this.iconActionClassMap = {};
    this.hasActivatedCustomNonPrimaryMap = {};
    this.dataSource.data!.content.forEach((element) => {
      this.customActionVisibilityMap[element.id] = this.customActions().map(
        (action) => {
          const newSignal = signal(false);
          action.isEnabled(element).subscribe((enabled) => {
            newSignal.set(enabled);
          });
          return newSignal;
        },
      );
      this.customActionActivationMap[element.id] = this.customActions().map(
        (action) =>
          signal(action.checkActive ? action.checkActive(element.id) : true),
      );
      this.customActionTextMap[element.id] = this.customActions().map(
        (action) => action.getText(element),
      );
      this.customActionClassMap[element.id] = this.customActions().map(
        (action) => action.getClass(element),
      );
      this.iconActionVisibilityMap[element.id] = this.iconActions().map(
        (action) => {
          const newSignal = signal(false);
          action.isEnabled(element).subscribe((enabled) => {
            newSignal.set(enabled);
          });
          return newSignal;
        },
      );
      this.iconActionActivationMap[element.id] = this.iconActions().map(
        (action) =>
          signal(action.checkActive ? action.checkActive(element.id) : true),
      );
      this.iconActionTextMap[element.id] = this.iconActions().map((action) =>
        action.getText(element),
      );
      this.iconActionClassMap[element.id] = this.iconActions().map((action) =>
        action.getClass(element),
      );
      this.hasActivatedCustomNonPrimaryMap[element.id] = computed(() => {
        const actions = this.customActions();
        return actions.some(
          (action, index) =>
            !action.primaryAction &&
            this.customActionActivationMap[element.id][index]() &&
            this.customActionVisibilityMap[element.id][index](),
        );
      });
    });
  }

  private setUpProcessedDataMirror(): void {
    if (!this.dataSource.count) {
      this.processedData = {};
      return;
    }
    const dataCopy = this.dataSource.data!.content.slice();

    // Get current columns from actualDisplayedColumns, excluding 'actions'
    const currentColumns = this.actualDisplayedColumns().filter(
      (c) => c.displayedProperty !== 'actions',
    );
    const currentSyncColumns = currentColumns.filter(
      (c) => c.pipe || !c.asyncPipe,
    );
    const currentAsyncColumns = currentColumns.filter(
      (c) => !c.pipe && c.asyncPipe,
    );

    dataCopy.forEach((element) => {
      // Load sync values directly
      const syncValues = currentSyncColumns.reduce(
        (valueObject, column) =>
          Object.assign(valueObject, {
            [column.displayedProperty]: signal<string | undefined>(
              this.getSyncDataAsString(
                element as Record<string, never>,
                column,
              ),
            ),
          }),
        {},
      );
      // Prepare async values as undefined
      this.processedData[element.id] = currentAsyncColumns.reduce(
        (valueObject, column) =>
          Object.assign(valueObject, {
            [column.displayedProperty]: signal<string | undefined>(undefined),
          }),
        syncValues,
      );
    });

    // load async values
    currentAsyncColumns.forEach((column) => {
      dataCopy.forEach((element) => {
        this.getAsyncDataByString(
          element as Record<string, never>,
          column,
        ).subscribe((data) => {
          this.setProcessedValue(element.id, column.displayedProperty, data);
        });
      });
    });
  }

  private buildColumnsSignature(columns: Column[]): string {
    return columns
      .map((column) =>
        [
          column.displayedProperty,
          column.name ?? '',
          column.sortingName,
          String(column.sortable),
          String(column.translateHeader),
          String(column.translate),
          String(!!column.template),
          String(column.invertSorting),
          column.pipe?.constructor?.name ?? '',
          column.asyncPipe?.constructor?.name ?? '',
        ].join('|'),
      )
      .join('||');
  }

  private setProcessedValue(
    id: string,
    displayedProperty: string,
    value: string | undefined,
  ) {
    if (!this.processedData[id]) {
      console.error(`No processed data object found for id: ${id}`);
      return;
    }
    this.processedData[id][displayedProperty].set(value);
  }

  private saveSortPageFilterSearch(): void {
    if (this.identifier) {
      const sortPageFilterState = {
        sort: this.sort.active,
        direction: this.sort.direction,
        pageSize: this.paginator.pageSize,
        // page: this.paginator.pageIndex,
        search: this.input?.nativeElement?.value ?? '',
      };
      localStorage.setItem(
        this.identifier + this.storageKeyAppendix,
        JSON.stringify(sortPageFilterState),
      );
    }
  }

  private resetQuery(): void {
    if (this.identifier) {
      localStorage.removeItem(this.identifier + this.storageKeyAppendix);
      this.sort.active = '';
      this.sort.direction = '';
      const queryParams = { ...this.route.snapshot.queryParams };
      delete queryParams[this.sortParamKey];
      delete queryParams[this.directionParamKey];
      delete queryParams[this.pageSizeParamKey];
      delete queryParams[this.pageParamKey];
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams,
          replaceUrl: true,
        })
        .then(() => {
          this.reloadOnError = false;
          this.loadSortPageFilterSearch();
        });
    }
  }

  private getSort(): SortDirection {
    const column = this.columns.find(
      (c) => c.displayedProperty === this.sort.active,
    );
    if (!column || !column.invertSorting) {
      return this.sort.direction;
    }
    return this.sort.direction === 'asc' ? 'desc' : 'asc';
  }

  private loadSortPageFilterSearch(): void {
    const params = this.route.snapshot.queryParams;
    const sort = this.isMainTable() ? params[this.sortParamKey] : undefined;
    const direction = this.isMainTable()
      ? params[this.directionParamKey]
      : undefined;
    const pageSize = this.isMainTable()
      ? params[this.pageSizeParamKey]
      : undefined;
    const page = this.isMainTable() ? params[this.pageParamKey] : undefined;
    const backupSort =
      this.defaultSortName ??
      this.columns.filter((c) => c.sortable)?.[0]?.sortingName ??
      'id';
    const backupDirection = this.defaultSortDirection ?? 'asc';
    if (this.isMainTable()) {
      if (sort) {
        this.sort.active = sort;
      }
      if (direction) {
        this.sort.direction = direction;
      }
      if (pageSize) {
        this.paginator.pageSize = pageSize;
      }
      if (page) {
        this.paginator.pageIndex = page;
        this.paginator.page.emit();
      }
    }
    if (this.identifier) {
      const sortPageFilterStateJson = localStorage.getItem(
        this.identifier + this.storageKeyAppendix,
      );
      if (sortPageFilterStateJson) {
        const sortPageFilterState = JSON.parse(sortPageFilterStateJson);
        if (sortPageFilterState) {
          this.sort.active = sort ?? sortPageFilterState.sort;
          this.sort.direction = direction ?? sortPageFilterState.direction;
          this.paginator.pageSize = pageSize ?? sortPageFilterState.pageSize;
          this.paginator.pageIndex = page ?? sortPageFilterState.page ?? 0;
          if (this.searchEnabled && sortPageFilterState.search && this.input) {
            this.input.nativeElement.value = sortPageFilterState.search;
          }
        }
      }
    }
    if (!this.sort.active) {
      this.sort.active = backupSort;
    }
    if (!this.sort.direction) {
      this.sort.direction = backupDirection;
    }
    this.sort.sortChange.emit();
    this.saveSortPageFilterSearch();
    // Trigger change detection to prevent ExpressionChangedAfterItHasBeenCheckedError
    // since sort values are set in ngAfterViewInit
    this.cdr.detectChanges();
  }

  private changeQueryParams(changeLocation = true): void {
    if (this.identifier && this.isMainTable()) {
      const queryParams: Params = {};
      queryParams[this.sortParamKey] = this.sort.active;
      queryParams[this.directionParamKey] = this.sort.direction;
      queryParams[this.pageSizeParamKey] = this.paginator.pageSize;
      queryParams[this.pageParamKey] = this.paginator.pageIndex;
      if (changeLocation) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams,
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      } else {
        this.router.navigate([], {
          queryParams,
          replaceUrl: true,
        });
      }
    }
  }

  getSyncDataAsString(o: RecursiveElement, column: Column): string | undefined {
    const data = this.getRawDataByString(o, column);
    if (column.pipe) {
      return column.pipe.transform(data) as string | undefined;
    } else if (Array.isArray(data)) {
      return data.length > 0 ? String(data[0]) : undefined;
    } else {
      return data as string | undefined;
    }
  }

  getAsyncDataByString(
    o: RecursiveElement,
    column: Column,
  ): Observable<string | undefined> {
    if (!column.asyncPipe) {
      console.error(
        `No asyncPipe found for column: ${column.displayedProperty}`,
      );
      return of(undefined);
    }

    const data = this.getRawDataByString(o, column);
    return column.asyncPipe.transform(data);
  }

  private getRawDataByString(o: RecursiveElement, column: Column): unknown {
    return resolveColumnValue(o, column.displayedProperty);
  }

  private buildDeletePreview(deleteId: string): DeletePreview | undefined {
    const item = this.dataSource.data?.content.find(
      (element) => element.id === deleteId,
    );
    if (!item) {
      return undefined;
    }

    const previewFields = this.actualDisplayedColumns()
      .filter((column) => column.displayedProperty !== 'actions')
      .map((column) => this.buildDeletePreviewField(item, column))
      .filter((field): field is DeletePreviewField => field !== undefined);

    if (!previewFields.length) {
      return undefined;
    }

    const [primary, ...details] = previewFields;
    return {
      primary,
      details: details.slice(0, 2),
    };
  }

  private buildDeletePreviewField(
    item: T,
    column: Column,
  ): DeletePreviewField | undefined {
    const renderValueAsHtml =
      !column.translate && !isResultColumn(column.displayedProperty);
    const processedValue =
      this.processedData[item.id]?.[column.displayedProperty]?.() ??
      this.getSyncPreviewValue(item, column) ??
      resolveColumnValue(item, column.displayedProperty);
    const value = this.normalizeDeletePreviewValue(
      processedValue,
      renderValueAsHtml,
    );

    if (!value) {
      return undefined;
    }

    const label = column.name ?? column.displayedProperty;

    return {
      label,
      translateLabel: !!column.name && column.translateHeader,
      value,
      translateValue: column.translate,
      renderValueAsHtml,
    };
  }

  private getSyncPreviewValue(item: T, column: Column): string | undefined {
    if (column.asyncPipe) {
      return undefined;
    }

    return this.getSyncDataAsString(
      item as unknown as RecursiveElement,
      column,
    );
  }

  private normalizeDeletePreviewValue(
    value: unknown,
    renderValueAsHtml: boolean,
  ): string | SafeHtml | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      return trimmedValue.length > 0 ? trimmedValue : undefined;
    }

    if (renderValueAsHtml) {
      return value as SafeHtml;
    }

    const trimmedValue = String(value).trim();
    return trimmedValue.length > 0 ? trimmedValue : undefined;
  }

  protected isResultColumn(column: Column): boolean {
    return isResultColumn(column.displayedProperty);
  }

  protected truncateForCell(value: string | undefined): string | undefined {
    return truncateCellValue(
      value,
      DataTableComponent.RESULT_CELL_TRUNCATE_LIMIT,
    );
  }

  protected needsTruncation(value: string | undefined): boolean {
    return isValueTruncated(
      value,
      DataTableComponent.RESULT_CELL_TRUNCATE_LIMIT,
    );
  }

  protected updateCustomFilter(
    element: AbstractFilterElement,
    filter: FilterEmitterType,
  ): void {
    if (filter.term) {
      if (this.soloFilterActive || element.isSolo) {
        this.customFilters = {};
        this.soloFilterActive = element.isSolo;
      }
      this.customFilters[filter.key] = filter.term;
    } else {
      delete this.customFilters[filter.key];
    }
    this.paginator.pageIndex = 0;
    this.saveSortPageFilterSearch();
    this.changeQueryParams();
    this.loadDataPage();
  }

  protected updateResultFilter(
    element: AbstractFilterElement,
    filter: { field: string; value: string } | null,
  ): void {
    if (filter) {
      // Format: fieldName:contains:value
      this.resultFilters[element.filterKey] =
        `${filter.field}:contains:${filter.value}`;
    } else {
      delete this.resultFilters[element.filterKey];
    }
    this.paginator.pageIndex = 0;
    this.saveSortPageFilterSearch();
    this.changeQueryParams();
    this.loadDataPage();
  }

  protected updateMultiResultFilters(
    element: AbstractFilterElement,
    filters: { field: string; value: string }[],
  ): void {
    // Clear existing filters for this element
    const keysToDelete = Object.keys(this.resultFilters).filter(
      (key) =>
        key.startsWith(element.filterKey + '_') || key === element.filterKey,
    );
    keysToDelete.forEach((key) => delete this.resultFilters[key]);

    // Add new filters with indexed keys
    filters.forEach((filter, index) => {
      const filterKey = `${element.filterKey}_${index}`;
      this.resultFilters[filterKey] =
        `${filter.field}:contains:${filter.value}`;
    });

    this.paginator.pageIndex = 0;
    this.saveSortPageFilterSearch();
    this.changeQueryParams();
    this.loadDataPage();
  }

  protected hasMultiFilterActive(filterKey: string): boolean {
    return Object.keys(this.resultFilters).some(
      (key) => key.startsWith(filterKey + '_') || key === filterKey,
    );
  }

  clearAllFilters(): void {
    this.customFilters = {};
    this.resultFilters = {};
    this.soloFilterActive = false;
  }

  emitAction(action: CustomAction<T>, id: string): void {
    if (action.isDelete) {
      this.deleteConfirmation(id, action.action);
    } else {
      action.emit(id);
    }
  }

  /**
   * Returns appropriate CSS classes for filter elements when positioned beside the search field.
   * Uses fixed widths instead of Bootstrap grid classes since we're not in a row context.
   */
  getBesideLayoutSize(size: FilterElementSize): string {
    switch (size) {
      case FilterElementSize.EXTRA_SMALL:
        return 'filter-beside-xs'; // ~120px
      case FilterElementSize.SMALL:
        return 'filter-beside-sm'; // ~150px
      case FilterElementSize.LARGE:
        return 'filter-beside-lg'; // ~250px
      case FilterElementSize.EXTRA_LARGE:
        return 'filter-beside-xl'; // ~300px
      case FilterElementSize.FULL_SIZE:
        return 'filter-beside-full'; // ~400px
      case FilterElementSize.MEDIUM:
      default:
        return 'filter-beside-md'; // ~200px
    }
  }

  show(event: Event, id: string): void {
    const cell = event.currentTarget;
    if (cell instanceof HTMLElement) {
      const selection = cell.ownerDocument.getSelection();
      // A click also fires when a drag finishes selecting text for copying.
      if (selection && !selection.isCollapsed && selection.toString().length) {
        for (let index = 0; index < selection.rangeCount; index++) {
          if (selection.getRangeAt(index).intersectsNode(cell)) {
            return;
          }
        }
      }
    }
    if (
      event.target instanceof HTMLElement &&
      this.clickActionBlacklist.indexOf(event.target.tagName.toLowerCase()) > -1
    ) {
      return;
    }
    this.showEvent.emit(id);
  }

  new(): void {
    this.newEvent.emit();
  }

  public deleteConfirmation(
    deleteId: string,
    deleteEvent: EventEmitter<string>,
  ): void {
    const deletePreview = this.buildDeletePreview(deleteId);

    this.deleteEvent = deleteEvent;
    this.deleteId = deleteId;
    this.modalRef = this.modalService.open(ConfirmationDialogComponent, {
      data: {
        title: 'delete?',
        message: 'delete_question',
        itemLabel: deletePreview?.primary.label,
        itemLabelTranslate: deletePreview?.primary.translateLabel ?? false,
        itemValue: deletePreview?.primary.value,
        itemValueTranslate: deletePreview?.primary.translateValue ?? false,
        itemValueRenderAsHtml:
          deletePreview?.primary.renderValueAsHtml ?? false,
        details: deletePreview?.details ?? [],
        cancelLabel: 'cancel',
        confirmLabel: 'delete',
        confirmButtonClass: 'btn-danger',
      },
    });

    this.modalRef.closed.subscribe((confirmed) => {
      if (confirmed === true) {
        this.deleteEvent?.emit(this.deleteId!);
      }
    });
  }

  hasShowListener(): boolean {
    return this.showEvent.observed;
  }

  setLoadInterval(interval: number): void {
    if (interval < 1000) {
      interval = 1000;
    }
    this.reloadInterval = interval;
  }

  openColumnConfiguration(): void {
    if (!this.identifier) {
      console.warn('Column configuration requires an identifier to be set');
      return;
    }

    const customFieldKeys = new Set(
      this.customFieldOptions().map((option) => option.key),
    );
    const columnSource = this.baseColumns.length
      ? this.baseColumns
      : this.columns;
    const seen = new Set<string>();
    const baseColumnOptions: ColumnOption[] = [];

    columnSource.forEach((col) => {
      const key = col.displayedProperty;
      if (key === 'actions' || customFieldKeys.has(key) || seen.has(key)) {
        return;
      }
      seen.add(key);
      baseColumnOptions.push({
        key,
        label: col.name || col.displayedProperty,
        selected: true,
        isCustom: false,
      });
    });

    const initialRef = this.modalService.open<
      ColumnConfigurationComponent,
      {
        tableId: string;
        baseColumns: ColumnOption[];
        customFieldOptions: ColumnOption[];
      },
      string[]
    >(ColumnConfigurationComponent, {
      data: {
        tableId: this.identifier,
        baseColumns: baseColumnOptions,
        customFieldOptions: [...this.customFieldOptions()],
      },
      size: 'lg',
    });

    initialRef.closed.subscribe((visibleColumns) => {
      if (visibleColumns) {
        this.columnConfigurationChanged.emit(visibleColumns);
      }
    });
  }
}
